import axios from 'axios';

import { getConfig } from '../common/config';
import { UpsellMessageType } from '../components/targeted-upsell/TargetedUpsell';
import { UpsellAnalytics } from '../hooks/useQualtricsAPI';

export const PERSONA_PATH = 'api/persona';
export const V2_UPSELL_MIDDLEWARE_PATH = 'api/upsell-middleware/pos';
export const UPSELL_URL = 'api/upsell-middleware';

const config = getConfig();

export interface LatestPeriodSequenceV2 {
  cell: string;
  deliveries: number;
  dataAccessEndDatePeriodSequence: string;
  latestPublishedPeriodSequence: string;
  dataAccessEndDatePeriodSequenceDisplay: string;
  latestPublishedPeriodSequenceDisplay: string;
  periodicity: string;
}

export interface TargetedUpsellPostBody {
  countryCode: string;
  itemGroupCode: string;
  dataPeriod: string | number;
}

export interface UpsellAnalyticsRequestBody {
  upsellName: string;
}

export interface UpsellAnalyticsRecord {
  cell: string;
  dataPeriod?: string;
  upsellName: string;
  upsellStatus: string;
  timeStamp: string;
}

export interface RetailKADMessageRequestBody {
  marketFilters: RetailKADMessageMarketFilter[];
}

export interface RetailKADMessageMarketFilter {
  itemGroupCode: string;
  countryCode: string;
}

export interface RetailKADMessageResponse {
  country: string;
  productGroup: string;
  itemGroupCode: string;
  countryCode: string;
  statement1: string;
  statement2: string;
  statement3: string;
  priority: string;
}

export const getSequencePeriodV2 = async (token: string): Promise<LatestPeriodSequenceV2[]> => {
  try {
    let headers = { Authorization: `Bearer ${token}`, 'X-Impersonate-Client': '' };
    const impersonation = window.localStorage.getItem('impersonation');
    if (impersonation) {
      headers = {
        ...headers,
        'X-Impersonate-Client': impersonation,
      };
    }

    const url = `${config.BASE_URL}${V2_UPSELL_MIDDLEWARE_PATH}`;
    const { data } = await axios.get<LatestPeriodSequenceV2[]>(`${url}/latest-period-seq`, {
      headers,
    });

    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

export const getLatestSequencePeriodForSubscription = async (token: string): Promise<LatestPeriodSequenceV2[]> => {
  return getSequencePeriodV2(token);
};

export const getUpsellMessageHandler = async (
  token: string,
  body: TargetedUpsellPostBody
): Promise<UpsellMessageType> => {
  try {
    let headers = { Authorization: `Bearer ${token}`, 'X-Impersonate-Client': '' };
    const impersonation = window.localStorage.getItem('impersonation');
    if (impersonation) {
      headers = {
        ...headers,
        'X-Impersonate-Client': impersonation,
      };
    }

    const url = `${config.BASE_URL}${UPSELL_URL}`;
    const { data } = await axios.post(`${url}/getUpsellMessage`, body, {
      headers,
    });

    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

export const getUpsellMessage = async (token: string, body: TargetedUpsellPostBody): Promise<UpsellMessageType> => {
  return getUpsellMessageHandler(token, body);
};

export const postUpsellAnalyticsService = async (
  token: string,
  body: UpsellAnalytics
): Promise<{ message: string }> => {
  try {
    const headers = { Authorization: `Bearer ${token}`, 'X-Impersonate-Client': '' };
    const url = `${config.BASE_URL}${UPSELL_URL}`;
    const { data } = await axios.post(`${url}/postUpsellAnalytics`, body, {
      headers,
    });

    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

export const postUpsellAnalytics = async (token: string, body: UpsellAnalytics): Promise<{ message: string }> => {
  return postUpsellAnalyticsService(token, body);
};

export const getUpsellAnalyticsService = async (
  token: string,
  analyticsDataRequestBody: UpsellAnalyticsRequestBody
): Promise<UpsellAnalyticsRecord[]> => {
  try {
    const headers = { Authorization: `Bearer ${token}`, 'X-Impersonate-Client': '' };

    const queryParams = new URLSearchParams({ ...analyticsDataRequestBody });

    const url = `${config.BASE_URL}${UPSELL_URL}`;
    const { data } = await axios.get<UpsellAnalyticsRecord[]>(`${url}/getUpsellAnalytics?${queryParams}`, {
      headers,
    });

    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

export const getRetailKADMessageService = async (
  token: string,
  body: RetailKADMessageRequestBody
): Promise<RetailKADMessageResponse[]> => {
  try {
    const headers = { Authorization: `Bearer ${token}`, 'X-Impersonate-Client': '' };

    const url = `${config.BASE_URL}${UPSELL_URL}`;
    const { data } = await axios.post(`${url}/getStaticUpsellData`, body, {
      headers,
    });

    return data;
  } catch (err: any) {
    throw new Error(`An error has occurred: ${err}`);
  }
};

export const getUpsellAnalytics = async (
  token: string,
  analyticsDataRequestBody: UpsellAnalyticsRequestBody
): Promise<UpsellAnalyticsRecord[]> => {
  return getUpsellAnalyticsService(token, analyticsDataRequestBody);
};
